import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SiteShell from "../components/site/SiteShell";
import useReveal from "../lib/useReveal";
import useSEO from "../lib/useSEO";
import { caseStudies, verticalPlaybooks } from "../lib/caseStudiesData";

export default function CaseStudiesPage() {
  useReveal();
  useSEO({
    title: "Case Studies — Proof of Performance Across Politics, Casinos, Sports & Retail",
    description: "Anonymized geofencing campaign results and vertical playbooks: political campaigns, casinos, stadium targeting for World Cup, baseball and basketball, and retail holiday promos.",
    path: "/case-studies",
  });
  return (
    <SiteShell>
      <section className="pt-32 pb-20 border-b border-white/5" data-testid="cs-hero">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="kicker mb-6">Case Studies</div>
          <h1 className="h-display text-white text-[48px] sm:text-[72px] lg:text-[96px] leading-[0.9]">
            PROOF OF<br /><span className="text-[#d4af37]">PERFORMANCE.</span>
          </h1>
          <p className="mt-8 max-w-[560px] text-white/70 text-[16px] leading-relaxed">
            Real campaigns across politics, gaming, sports and retail — anonymized
            to protect client confidentiality, reported exactly as the dashboards
            showed them.
          </p>
        </div>
      </section>

      {/* Anonymized wins */}
      <section className="py-24" data-testid="cs-wins">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="kicker mb-4">Recent Wins</div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-14">
            BY THE <span className="text-[#d4af37]">NUMBERS.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#d4af37]/10">
            {caseStudies.map((c, i) => (
              <div
                key={c.industry}
                className="bg-[#0b0b0b] hover:bg-[#111111] p-10 lg:p-12 transition-colors duration-500 reveal"
                data-testid={`cs-win-card-${i}`}
              >
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">{c.industry}</div>
                <div className="text-[12px] text-white/40 tracking-wider mb-8">{c.context}</div>
                <div className="h-display text-white text-[44px] leading-none mb-1">{c.headline}</div>
                <div className="text-[13px] text-white/60 mb-8">{c.headlineLabel}</div>
                <ul className="space-y-3">
                  {c.stats.map((s) => (
                    <li key={s.label} className="flex items-baseline justify-between gap-4 border-b border-white/5 pb-3">
                      <span className="text-[12px] tracking-wider uppercase text-white/45">{s.label}</span>
                      <span className="text-[15px] text-[#d4af37] font-medium">{s.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vertical playbooks */}
      <section className="py-24 bg-[#0d0d0d] border-y border-[#d4af37]/10" data-testid="cs-verticals">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="kicker mb-4">Vertical Playbooks</div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-4">
            BUILT FOR YOUR <span className="text-[#d4af37]">ARENA.</span>
          </h2>
          <p className="text-white/50 text-[13px] tracking-wide mb-16 max-w-[560px]">
            Representative campaigns. Client identities and exact figures anonymized
            to protect confidentiality.
          </p>
          <div className="space-y-px bg-[#d4af37]/10">
            {verticalPlaybooks.map((v, i) => (
              <div
                key={v.id}
                className="bg-[#0b0b0b] p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-10 reveal"
                data-testid={`cs-vertical-${v.id}`}
              >
                <div className="lg:col-span-7">
                  <div className="text-[12px] tracking-[0.25em] text-white/40 mb-3">0{i + 1} — {v.kicker.toUpperCase()}</div>
                  <h3 className="h-display text-white text-[30px] sm:text-[40px] mb-6">
                    {v.title.replace(".", "")}<span className="text-[#d4af37]">.</span>
                  </h3>
                  <p className="text-white/70 text-[15px] leading-relaxed mb-8">{v.desc}</p>
                  <ul className="space-y-3">
                    {v.tactics.map((t) => (
                      <li key={t} className="flex items-start gap-3 text-[13px] text-white/70">
                        <span className="mt-2 w-1 h-1 bg-[#d4af37] inline-block shrink-0" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-5 lg:border-l lg:border-[#d4af37]/10 lg:pl-10 flex flex-col justify-center gap-8">
                  {v.stats.map((s) => (
                    <div key={s.label}>
                      <div className="h-display text-[#d4af37] text-[40px] leading-none">{s.value}</div>
                      <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 mt-2">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="cs-cta">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <div className="kicker mb-6">Your Turn</div>
          <h3 className="h-display text-white text-[32px] sm:text-[48px]">
            LET'S WRITE YOUR <span className="text-[#d4af37]">CASE STUDY.</span>
          </h3>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-5">
            <Link to="/campaign-builder" className="btn-gold btn-label" data-testid="cs-builder-cta">
              Build Your Campaign <ArrowRight size={16} strokeWidth={1.6} />
            </Link>
            <Link to="/geofencing-101" className="btn-outline-gold btn-label" data-testid="cs-101-link">
              New To Geofencing? Start Here
            </Link>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
